/**
 * Every Document Form in the project — the way IN to the `E` designer.
 *
 * A document form is deliberately not a navigation object: it is a FACE on a
 * transaction, reached at run time as the fourth view of that transaction's own
 * screen. That is the right runtime shape and it leaves a hole at design time,
 * because the workbench's Prototype tree is driven by the navigation — so a
 * form with no menu entry has no node to select, and its designer has no door.
 *
 * The same hole the Schedulers have, answered the same way: a project-level
 * list under Advanced. This is that list, plus the one thing a list like this
 * must also do — let somebody CREATE the first one, since there is no nav node
 * to add either.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  listDocumentForms, saveDocumentForm, type DocumentFormSummary,
} from '@/api/er'
import { objectCatalog } from '@/api/designers'

export default function DocumentFormListPage() {
  const params = useParams<{ project?: string }>()
  const project = params.project || ''
  const navigate = useNavigate()

  const [forms, setForms] = useState<DocumentFormSummary[]>([])
  const [objects, setObjects] = useState<{ obj_name: string }[]>([])
  const [visuals, setVisuals] = useState<{ obj_name: string }[]>([])
  const [busy, setBusy] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newAnchor, setNewAnchor] = useState('')

  const load = useCallback(() => {
    if (!project) return
    setBusy(true)
    listDocumentForms(project)
      .then(setForms)
      .catch((exc) => toast.error(exc?.response?.data?.detail
        || 'The document forms could not be listed.'))
      .finally(() => setBusy(false))
  }, [project])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!project) return
    // Both kinds of source a document form can be a face on: a transaction
    // (one record) or a visual (a result set, one document per identity).
    Promise.allSettled([
      objectCatalog(project, 'T'),
      objectCatalog(project, 'V'),
    ]).then(([t, v]) => {
      setObjects(t.status === 'fulfilled' ? (t.value.objects || []) : [])
      setVisuals(v.status === 'fulfilled' ? (v.value.objects || []) : [])
    })
  }, [project])

  const open = (objName: string) =>
    navigate(`/designers/document-form/${encodeURIComponent(project)}`
      + `/${encodeURIComponent(objName)}`)

  /** Anchor → the forms on it, so a transaction with two faces reads as one row. */
  const byAnchor = useMemo(() => {
    const out = new Map<string, DocumentFormSummary[]>()
    for (const f of forms) {
      const key = (f.anchor_obj || '—').toLowerCase()
      out.set(key, [...(out.get(key) || []), f])
    }
    return out
  }, [forms])

  const create = async () => {
    const name = newName.trim().replace(/\s+/g, '_').toUpperCase()
    if (!name) { toast.error('Give the document form a name.'); return }
    if (!newAnchor) { toast.error('Choose the source it records against.'); return }
    if (forms.some((f) => f.obj_name.toUpperCase() === name)) {
      toast.error(`'${name}' already exists in this project.`); return
    }
    setBusy(true)
    try {
      // The server normalises whatever it is sent, so the client posts the
      // MINIMUM that says what this form is and lets the normaliser fill in
      // the rest — rather than shipping a second copy of the default shape
      // that can drift from `er_model.default_model`.
      const isVisual = visuals.some(
        (v) => v.obj_name.toUpperCase() === newAnchor.toUpperCase())
      await saveDocumentForm(project, name, {
        OBJ_NAME: name,
        OBJ_TYPE: 'E',
        form: isVisual
          // A visual source is declared as a SOURCE rather than as an
          // `anchor`: the legacy anchor block means "create the record when
          // there is none", which a visual can never do. The normaliser fills
          // in the rest of the shape from here.
          ? {
            title: newName.trim(),
            governance: 'none',
            transactions: [{
              obj_name: newAnchor, kind: 'visual', role: 'primary',
              on_missing: 'skip',
            }],
          }
          : {
            title: newName.trim(),
            governance: 'none',
            anchor: { mode: 'create', obj_name: newAnchor },
            capture: { mode: 'typed', obj_name: newAnchor },
          },
      })
      toast.success(`'${name}' created`)
      setCreating(false)
      setNewName('')
      setNewAnchor('')
      open(name)
    } catch (exc) {
      const detail = (exc as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      toast.error(detail || 'The document form could not be created.')
    } finally {
      setBusy(false)
    }
  }

  if (!project) {
    return <Empty>Pick a project first.</Empty>
  }

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16,
        marginBottom: 18 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1.3125rem' }}>Document Forms</h2>
          <p style={{ margin: '6px 0 0', fontSize: '0.9125rem',
            color: 'var(--color-text-secondary)', maxWidth: 720 }}>
            A document-shaped way into a transaction: narrative, merge fields
            pulled from the record, boxes an operator fills. A form is offered
            at run time as a view of the transaction it records against — it has
            no menu entry of its own, which is why it is listed here.
          </p>
        </div>
        <button type="button" onClick={() => setCreating((v) => !v)}
          style={buttonStyle(true)}>
          {creating ? 'Cancel' : 'New document form'}
        </button>
      </div>

      {creating && (
        <div style={{
          border: '1px solid var(--color-border)', borderRadius: 8,
          padding: 14, marginBottom: 18, display: 'grid', gap: 12,
          background: 'var(--color-bg-secondary)',
        }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={labelStyle}>Name</span>
            <input value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Deal Sheet" style={inputStyle} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={labelStyle}>Records against</span>
            <select value={newAnchor}
              onChange={(e) => setNewAnchor(e.target.value)} style={inputStyle}>
              <option value="">— choose a source —</option>
              {objects.length > 0 && (
                <optgroup label="Transactions">
                  {objects.map((o) => (
                    <option key={`t-${o.obj_name}`} value={o.obj_name}>
                      {o.obj_name}
                    </option>
                  ))}
                </optgroup>
              )}
              {visuals.length > 0 && (
                <optgroup label="Visuals">
                  {visuals.map((o) => (
                    <option key={`v-${o.obj_name}`} value={o.obj_name}>
                      {o.obj_name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <span style={hintStyle}>
              On a <strong>transaction</strong> the form becomes a view of it
              and writes through it; nothing about the transaction changes. On
              a <strong>visual</strong> it becomes a document over the
              visual&rsquo;s rows — read only, one document per identity you
              name, with any extra fields kept on the document.
            </span>
          </label>
          <div>
            <button type="button" onClick={() => void create()} disabled={busy}
              style={buttonStyle(true)}>Create and open</button>
          </div>
        </div>
      )}

      {busy && !forms.length ? (
        <Empty>Loading…</Empty>
      ) : !forms.length ? (
        <Empty>
          No document forms yet. Create one to give a transaction a
          document-shaped face — an inspection sheet, an intake form, a deal
          sheet — without changing the transaction itself.
        </Empty>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {[...byAnchor.entries()].map(([anchor, group]) => (
            <div key={anchor}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 700,
                letterSpacing: 0.4, textTransform: 'uppercase',
                color: 'var(--color-text-secondary)', margin: '6px 2px' }}>
                {group[0].anchor_obj || 'Not anchored'}
              </div>
              {group.map((form) => (
                <button
                  key={form.obj_name}
                  type="button"
                  onClick={() => open(form.obj_name)}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    border: `1px solid ${form.error ? '#ef4444'
                      : 'var(--color-border)'}`,
                    borderRadius: 8, padding: '10px 12px', marginBottom: 6,
                    background: 'var(--color-bg)', color: 'var(--color-text)',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}
                >
                  <span className="material-icons"
                    style={{ fontSize: 21, color: 'var(--color-text-secondary)' }}>
                    article
                  </span>
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>
                      {form.title || form.obj_name}
                    </span>
                    <span style={{ color: 'var(--color-text-secondary)',
                      fontSize: '0.8425rem', marginLeft: 8 }}>
                      {form.obj_name}
                    </span>
                    {/* A form whose governance claim does not hold is LISTED
                        with its error rather than hidden — it is the one
                        somebody has to go and fix. */}
                    {form.error ? (
                      <span style={{ display: 'block', fontSize: '0.8425rem',
                        color: '#ef4444', marginTop: 3 }}>{form.error}</span>
                    ) : (form.problems || []).length ? (
                      /* A form that will REFUSE to open, or that would lose
                         what somebody types into it, is listed with what is
                         wrong — the same reasoning as the governance error
                         above: it is the one somebody has to go and fix, and
                         a form that looks fine in a list and fails on the shop
                         floor is the worst version of this screen. */
                      <span style={{ display: 'block', fontSize: '0.8425rem',
                        color: (form.problems || []).some((pb) => pb.blocking)
                          ? '#ef4444' : '#b45309',
                        marginTop: 3 }}>
                        {(form.problems || [])[0].message}
                        {(form.problems || []).length > 1
                          && ` (+${(form.problems || []).length - 1} more)`}
                      </span>
                    ) : form.description ? (
                      <span style={{ display: 'block', fontSize: '0.8425rem',
                        color: 'var(--color-text-secondary)', marginTop: 3 }}>
                        {form.description}
                      </span>
                    ) : null}
                  </span>
                  {!form.error && (
                    <>
                      <Tag>{form.governance}</Tag>
                      <Tag>{form.capture_mode}</Tag>
                      {(form.transactions || []).length > 1 && (
                        <Tag>{(form.transactions || []).length} transactions</Tag>
                      )}
                      <Tag>{form.templates} template{form.templates === 1 ? '' : 's'}</Tag>
                    </>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      border: '1px dashed var(--color-border)', borderRadius: 8,
      padding: '24px 18px', fontSize: '0.9225rem',
      color: 'var(--color-text-secondary)', maxWidth: 660,
    }}>{children}</div>
  )
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: 0.5,
      padding: '2px 7px', borderRadius: 10, whiteSpace: 'nowrap',
      background: 'var(--color-bg-secondary)',
      color: 'var(--color-text-secondary)',
    }}>{children}</span>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)',
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
}

const inputStyle: React.CSSProperties = {
  height: 30, padding: '0 8px', borderRadius: 6, fontSize: '0.9025rem',
  border: '1px solid var(--color-border-strong)', background: 'var(--color-input-bg)',
  color: 'var(--color-text)', width: '100%', maxWidth: 420,
  boxSizing: 'border-box',
}

function buttonStyle(primary: boolean): React.CSSProperties {
  return {
    height: 30, padding: '0 14px', borderRadius: 6, fontSize: '0.8625rem',
    cursor: 'pointer',
    border: `1px solid ${primary ? 'var(--color-accent)' : 'var(--color-border)'}`,
    background: primary ? 'var(--color-accent)' : 'var(--color-bg)',
    color: primary ? '#fff' : 'var(--color-text)',
  }
}
